import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Award, Users, Globe, Heart, Target, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { mockData } from '../mock';

const About = () => {
  const { founder, team } = mockData;
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});
  const [isRossanaModalOpen, setIsRossanaModalOpen] = useState(false);
  const [isAlejandraModalOpen, setIsAlejandraModalOpen] = useState(false);
  const [isAliciaModalOpen, setIsAliciaModalOpen] = useState(false);
  const [isCarmenModalOpen, setIsCarmenModalOpen] = useState(false);

  const handleRossanaClick = () => {
    setIsRossanaModalOpen(true);
  };

  const closeRossanaModal = () => {
    setIsRossanaModalOpen(false);
  };

  const handleAlejandraClick = () => {
    setIsAlejandraModalOpen(true);
  };

  const closeAlejandraModal = () => {
    setIsAlejandraModalOpen(false);
  };

  const handleAliciaClick = () => {
    setIsAliciaModalOpen(true);
  };

  const closeAliciaModal = () => {
    setIsAliciaModalOpen(false);
  };

  const handleCarmenClick = () => {
    setIsCarmenModalOpen(true);
  };

  const closeCarmenModal = () => {
    setIsCarmenModalOpen(false);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all sections
    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const stats = [
    { icon: Users, number: "500+", label: "Lives Transformed" },
    { icon: Globe, number: "15", label: "Countries Reached" },
    { icon: Award, number: "15+", label: "Years Experience" },
    { icon: Heart, number: "98%", label: "Success Rate" }
  ];

  const values = [
    {
      title: "Compassion",
      description: "Every interaction is guided by deep understanding, empathy, and genuine care for your wellbeing.",
      icon: Heart
    },
    {
      title: "Integrity",
      description: "We maintain the highest ethical standards in all our programs, ensuring trust and transparency.",
      icon: CheckCircle
    },
    {
      title: "Empowerment",
      description: "We believe in your inherent strength and capacity for growth, fostering independence and confidence.",
      icon: Target
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Team Section */}
      <section 
        id="team"
        ref={el => sectionRefs.current.team = el}
        className={`py-20 bg-gray-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('team') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Expert Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the dedicated professionals who make transformation possible through their expertise, compassion, and commitment.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <Card 
                key={index} 
                className={`text-center group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 ${
                  member.name === "Rossana Auditore" || member.name === "Alejandra Benítez" || member.name === "Alicia Díaz Nilo" || member.name === "Carmen Gloria Rodriguez" ? "cursor-pointer" : ""
                }`}
                onClick={member.name === "Rossana Auditore" ? handleRossanaClick : 
                        member.name === "Alejandra Benítez" ? handleAlejandraClick :
                        member.name === "Alicia Díaz Nilo" ? handleAliciaClick :
                        member.name === "Carmen Gloria Rodriguez" ? handleCarmenClick : undefined}
              >
                <div className="p-6 space-y-4">
                  <div className="relative">
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="w-24 h-24 rounded-full mx-auto object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                    <p className="text-sm font-medium mb-2" style={{ color: '#6a6acb' }}>
                      {member.role}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                    {(member.name === "Rossana Auditore" || member.name === "Alejandra Benítez" || member.name === "Alicia Díaz Nilo" || member.name === "Carmen Gloria Rodriguez") && (
                      <p className="text-xs text-gray-500 mt-2 italic">Click to learn more</p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section 
        id="mission"
        ref={el => sectionRefs.current.mission = el}
        className={`py-20 bg-gray-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('mission') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                At Brain Waves Ventures, we provide those tools. Our programs integrate therapy-based coaching, structured strategies, and immersive experiences to create measurable, lasting progress. In a confidential setting, we combine expertise, cultural awareness, and action-oriented methods—helping you navigate change with clarity and confidence.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1636986905406-758b0e280f49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHx3b21lbiUyMGVtcG93ZXJtZW50fGVufDB8fHx8MTc1OTUxMDUxMXww&ixlib=rb-4.1.0&q=85"
                alt="Community connection"
                className="rounded-2xl shadow-2xl w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section 
        id="values"
        ref={el => sectionRefs.current.values = el}
        className={`py-20 bg-gray-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('values') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core principles guide everything we do and ensure that every program reflects our commitment to healing and growth.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center p-8 hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="space-y-6">
                    <div className="flex justify-center">
                      <div 
                        className="p-4 rounded-full"
                        style={{ backgroundColor: 'rgba(101, 131, 226, 0.1)' }}
                      >
                        <IconComponent className="h-8 w-8" style={{ color: 'rgba(50, 57, 241, 1)' }} />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="cta"
        ref={el => sectionRefs.current.cta = el}
        className={`bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('cta') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="w-full h-px bg-black"></div>
        <div className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Ready to Begin Your <span className="relative inline-block">
                <span className="relative z-10">Transformation</span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-200 to-purple-200 opacity-30 rounded-lg transform -rotate-1"></span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 opacity-50 rounded-lg transform rotate-1"></span>
              </span>?
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Join our global community of empowered women and professionals. Your journey to healing, growth, and connection starts today.
            </p>
            <button 
              onClick={() => {
                navigate('/contact');
                window.scrollTo(0, 0);
              }}
              className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
              style={{ 
                borderColor: '#6a6acb', 
                color: '#6a6acb',
                backgroundColor: 'transparent'
              }} 
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#6a6acb';
                e.target.style.color = 'white';
              }} 
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6a6acb';
              }}
            >
              Get Started Today →
            </button>
          </div>
        </div>
        <div className="w-full h-px bg-black"></div>
      </section>

      {/* Rossana Modal */}
      <Dialog open={isRossanaModalOpen} onOpenChange={setIsRossanaModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Rossana Auditore
            </DialogTitle>
            <p className="text-lg font-semibold" style={{ color: 'rgba(127, 81, 244, 1)' }}>
              Founder & Lead Therapist
            </p>
          </DialogHeader>
          
          <div className="mt-6 space-y-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <img 
                  src="/rosanna.jpeg"
                  alt="Rossana Auditore"
                  className="w-32 h-32 rounded-full object-cover mx-auto md:mx-0"
                />
              </div>
              <div className="flex-1">
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    Rossana Auditore is a multidisciplinary professional whose career bridges medicine, international business, and psychology. Having grown up in a country marked by political unrest, she developed an early understanding of resilience and the importance of community. After completing five years of medical studies, she transitioned into international trade and financial compliance, mastering complex global systems, and later earned a degree in psychology to combine analytical expertise with a deep understanding of human behavior.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    A seasoned solo traveler, Rossana has explored over 55 countries, lived and worked in five nations, and is fluent in English, Spanish, and Portuguese. Her global experience informs her inclusive, results-driven approach to coaching, leadership, and program design.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Her perspective deepened profoundly when she adopted a three-day-old baby with special needs. This personal journey has brought her face-to-face with the realities of developmental disorders, caregiving challenges, and the resilience required of single mothers. It has further fueled her commitment to building systems of support that are both compassionate and pragmatic.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Through Brain Waves, Rossana leads The International Women's Connection and The Safe Space—initiatives supporting women navigating transitions and recovery, particularly those who do not fit traditional "victim" narratives but have endured abuse and adversity. Her mission is to create confidential, structured programs that transform resilience into action, empowering individuals to reclaim their lives with clarity, confidence, and strength.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alejandra Modal */}
      <Dialog open={isAlejandraModalOpen} onOpenChange={setIsAlejandraModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Alejandra Benítez
            </DialogTitle>
            <p className="text-lg font-semibold" style={{ color: '#6a6acb' }}>
              Clinical Psychologist
            </p>
          </DialogHeader>
          
          <div className="mt-6 space-y-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <img 
                  src="/alejandra.jpeg"
                  alt="Alejandra Benítez"
                  className="w-32 h-32 rounded-full object-cover mx-auto md:mx-0"
                />
              </div>
              <div className="flex-1">
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    Alejandra Benítez is a Clinical Psychologist with more than 30 years of experience dedicated to supporting people as they navigate emotional, relational, and psychological challenges. Trained in Cognitive Behavioral Therapy, she holds both a postgraduate degree and a Master's in CBT from the University of Chile—one of South America's most prestigious academic institutions.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Throughout her career, Alejandra has accompanied her clients through experiences of anxiety, depression, trauma, and major life transitions, as well as those seeking personal growth and greater emotional well-being. She is known for combining evidence-based methods with a warm, empathetic approach that empowers her clients to build resilience, strengthen their inner voice, and rediscover balance in their lives.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Her personal story reflects the same perseverance she brings to her practice. While raising two daughters on her own, she pursued advanced studies and built a career that blends professional excellence with deep human understanding. Today, her daughters are thriving—one as an international lawyer and the other preparing to become a special needs specialist—reflecting the values of strength, dedication, and compassion she embodies.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alicia Modal */}
      <Dialog open={isAliciaModalOpen} onOpenChange={setIsAliciaModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Alicia Díaz Nilo
            </DialogTitle>
            <p className="text-lg font-semibold" style={{ color: '#6a6acb' }}>
              Social Worker & Victim Protection Specialist
            </p>
          </DialogHeader>
          
          <div className="mt-6 space-y-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <img 
                  src="/alicia.jpeg"
                  alt="Alicia Díaz Nilo"
                  className="w-32 h-32 rounded-full object-cover mx-auto md:mx-0"
                />
              </div>
              <div className="flex-1">
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    Alicia Díaz Nilo is a social worker and a recognized authority in victim protection, gender equity, inclusive labor practices, and social dialogue. A certified Gender and Pay Equity Auditor, she has dedicated her career to building rights-based systems that protect the most vulnerable and promote institutional accountability.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    As National Director of Victim and Witness Services at the Public Prosecutor's Office of Chile, she led the country's largest protection division, overseeing more than 200,000 cases per year related to gender-based violence, child abuse, trafficking, and other serious crimes. Her leadership transformed the national model for victim care, integrating innovation, accessibility, and trauma-informed practices.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Previously, she served as Social Dialogue Officer at the International Labour Organization (ILO) and has advised governments, corporations, and civil society organizations across Latin America as a senior consultant in gender, inclusion, and social development.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    She currently sits on the Board of the Social Chamber of the Forest Stewardship Council and directs Almudena Comunidad, a healing and advocacy collective for women affected by gender-based violence. She also serves as a consultant to The International Women's Connection and The Safe Space, two global initiatives dedicated to empowering women through community-building, and transformative justice.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Carmen Modal */}
      <Dialog open={isCarmenModalOpen} onOpenChange={setIsCarmenModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Carmen Gloria Rodriguez
            </DialogTitle>
            <p className="text-lg font-semibold" style={{ color: '#6a6acb' }}>
              Public Health Manager
            </p>
          </DialogHeader>
          
          <div className="mt-6 space-y-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <img 
                  src="/carmen.jpeg"
                  alt="Carmen Gloria Rodriguez"
                  className="w-32 h-32 rounded-full object-cover mx-auto md:mx-0"
                    />
                  </div>
              <div className="flex-1">
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    Carmen Gloria Rodriguez is a seasoned Public Health Manager with over 30 years of experience in community health, disease education, and prevention. Holding a Master of Public Health from Universidad de Puerto Rico, she specializes in sexually transmitted and contagious diseases.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Throughout her career, Carmen Gloria has successfully managed large-scale vaccination campaigns for HIV and led initiatives to reduce contagious diseases in underserved communities. Her achievements include implementing new safety standards for public spaces and developing comprehensive health education programs for schools.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    As a Quality Improvement Manager, she elevated Borinquen Center to one of Miami's top health organizations. After a fulfilling 15-year tenure as an online and in-person college professor, Carmen Gloria expanded her own business, providing government certificates to thousands of private businesses nationally and internationally.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Now, as a mentor, coach, and business consultant in soft skills and leadership, Carmen Gloria continues her passion for service, applying the five P's of Public Health: Prevention, Protection, Promotion, Policy, and Partnership. Her dedication to health equity and community resilience remains unwavering.
                  </p>
                </div>
          </div>
        </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default About;